// Botão de microfone: falar em vez de digitar.
//
// Por que NÃO é o Whisper do OpenRouter: o OpenRouter serve modelos de texto e
// visão; transcrição de áudio não está no catálogo dele. Daria para mandar o
// áudio a um modelo multimodal, mas isso custaria por minuto, exigiria chave,
// exigiria subir o áudio da paciente para um terceiro — e ainda assim seria
// mais lento do que o que o próprio aparelho já faz de graça.
//
// O Chrome do Android reconhece voz em português nativamente, sem chave e sem
// custo. É o que este botão usa. Onde não houver suporte, o botão simplesmente
// não aparece, e o microfone do teclado do Android continua funcionando em
// qualquer campo — é o mesmo resultado por outro caminho.
//
// ATENÇÃO, e está dito na tela: o reconhecimento do Chrome manda o áudio para
// servidor do Google. Não dite nome, documento ou leito da paciente.
import { useEffect, useRef, useState } from 'react';
import './ditar.css';

/** Tipos mínimos da Web Speech API — o TypeScript não traz os oficiais. */
interface ResultadoFala {
  isFinal: boolean;
  0: { transcript: string };
}
interface EventoFala extends Event {
  resultIndex: number;
  results: { length: number; [i: number]: ResultadoFala };
}
interface Reconhecedor extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: EventoFala) => void) | null;
  onerror: ((e: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
}
type FabricaDeReconhecedor = new () => Reconhecedor;

function fabrica(): FabricaDeReconhecedor | null {
  const janela = window as unknown as {
    SpeechRecognition?: FabricaDeReconhecedor;
    webkitSpeechRecognition?: FabricaDeReconhecedor;
  };
  return janela.SpeechRecognition ?? janela.webkitSpeechRecognition ?? null;
}

interface Props {
  /** Texto que já está no campo — o ditado é acrescentado ao que existe. */
  valor: string;
  aoMudar: (texto: string) => void;
  rotulo?: string;
}

export function Ditar({ valor, aoMudar, rotulo = 'ditar' }: Props) {
  const [ouvindo, setOuvindo] = useState(false);
  const [erro, setErro] = useState('');
  const reconhecedor = useRef<Reconhecedor | null>(null);
  // O valor vive numa ref para o callback do reconhecedor não congelar o texto
  // que havia quando o microfone foi ligado.
  const valorAgora = useRef(valor);
  valorAgora.current = valor;

  const suportado = fabrica() !== null;

  useEffect(() => () => reconhecedor.current?.stop(), []);

  function alternar() {
    if (ouvindo) {
      reconhecedor.current?.stop();
      return;
    }

    const Fabrica = fabrica();
    if (!Fabrica) return;

    const r = new Fabrica();
    r.lang = 'pt-BR';
    // Frase por frase: quem dita no corredor faz pausa, e parar a cada pausa
    // obrigaria a tocar no botão de novo no meio da anotação.
    r.continuous = true;
    r.interimResults = false;

    r.onresult = (evento) => {
      let novo = '';
      for (let i = evento.resultIndex; i < evento.results.length; i++) {
        const item = evento.results[i];
        if (item?.isFinal) novo += item[0].transcript;
      }
      if (!novo.trim()) return;
      const antes = valorAgora.current.trim();
      aoMudar(antes ? `${antes} ${novo.trim()}` : novo.trim());
    };

    r.onerror = (evento) => {
      const codigo = (evento as { error?: string }).error;
      setErro(
        codigo === 'not-allowed'
          ? 'sem permissão para o microfone'
          : codigo === 'no-speech'
            ? 'não ouvi nada'
            : 'não consegui ouvir agora',
      );
      setOuvindo(false);
    };

    r.onend = () => setOuvindo(false);

    try {
      r.start();
      reconhecedor.current = r;
      setErro('');
      setOuvindo(true);
    } catch {
      setErro('não consegui abrir o microfone');
    }
  }

  // Sem suporte, nada de botão quebrado na tela: o microfone do teclado do
  // Android faz o mesmo serviço em qualquer campo.
  if (!suportado) return null;

  return (
    <span className="ditar">
      <button
        type="button"
        className={`ditar__botao${ouvindo ? ' ditar__botao--ouvindo' : ''}`}
        onClick={alternar}
        aria-label={ouvindo ? 'parar de ditar' : rotulo}
        title={ouvindo ? 'toque para parar' : rotulo}
      >
        {ouvindo ? (
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
            <rect x="2" y="2" width="12" height="12" rx="2" fill="currentColor" />
          </svg>
        ) : (
          // SVG e nao emoji: o desenho do microfone muda de fonte para fonte e
          // no Android sai pequeno demais para virar alvo de toque.
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <rect x="9" y="2.5" width="6" height="11" rx="3" />
            <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
            <path d="M12 17.5V21" />
          </svg>
        )}
      </button>
      {ouvindo && <span className="ditar__aviso">ouvindo… toque para parar</span>}
      {erro && <span className="ditar__erro">{erro}</span>}
    </span>
  );
}
